import { trackStatsEvent, useStatsLoaded } from "../utils/statsAnalytics";
import { useAuth } from "../Context/AuthContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { completeTwitchConnection, disconnectTwitch, fetchTwitchChannelStats, startTwitchConnection } from "../services/twitchStats";
import { summarizeBroadcasts, TWITCH_PERIODS } from "../utils/twitchAnalytics";
import { StatsAccountBar, StatsState, StatsError, StatsLoading } from "./StatsUI";
import TwitchBroadcasts from "./TwitchBroadcasts";



export default function TwitchStats({ visible = true }) {
  const { currentUser } = useAuth();
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  useStatsLoaded("twitch", stats, stats?.broadcasts?.length || 0, visible);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);
  const generation = useRef(0);
  const oauthReturn = useRef(new URLSearchParams(window.location.hash.slice(1)).has("access_token"));
  const videos = useMemo(() => summarizeBroadcasts(stats?.broadcasts ?? null, days, stats ? new Date(stats.updatedAt).getTime() : Date.now()).broadcasts, [stats, days]);

  const refresh = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true); setError("");
    try {
      const result = await fetchTwitchChannelStats(currentUser);
      if (request === generation.current) setStats(result);
    } catch (failure) {
      if (request === generation.current) setError(failure.message || "Your Twitch broadcasts could not be loaded.");
    } finally { if (request === generation.current) setLoading(false); }
  }, [currentUser]);

  useEffect(() => {
    let active = true;
    completeTwitchConnection().then((isConnected) => {
      if (!active) return;
      setConnected(isConnected);
      if (isConnected && oauthReturn.current) trackStatsEvent("stats_connection_complete", { platform: "twitch" });
      if (isConnected) refresh();
      else setLoading(false);
    }).catch((failure) => { if (active) { setError(failure.message); setLoading(false); } });
    return () => { active = false; generation.current += 1; };
  }, [refresh]);

  useEffect(() => {
    if (!connected) return undefined;
    const timer = window.setInterval(() => refresh(), 50 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, [connected, refresh]);

  const connect = () => {
    setError("");
    try { startTwitchConnection(); } catch (failure) { setError(failure.message); }
  };
  const disconnect = () => {
    generation.current += 1;
    disconnectTwitch(); setConnected(false); setStats(null); setError(""); setLoading(false);
  };

  return <section aria-label="Twitch channel analytics">
    {!connected ? <StatsState platform="Twitch" loading={loading} error={error} onConnect={connect} /> : <>
      <StatsAccountBar handle={stats?.user.display_name || "Your Twitch channel"} picture={stats?.user.profile_image_url} loading={loading} onRefresh={refresh} onReconnect={error ? connect : undefined} reconnectLabel="Reconnect Twitch" onDisconnect={disconnect} />
      {error && <StatsError onRetry={refresh} />}
      {loading && !stats && <StatsLoading />}
      {stats && <>
        <div className="px-4 py-5 sm:px-5"><p className="stats-control-label">Broadcasts from the last</p><div role="group" aria-label="Broadcast period" className="stats-periods">
          {TWITCH_PERIODS.map((period) => <button key={period} type="button" aria-pressed={days === period} onClick={() => setDays(period)} aria-label={`Last ${period} days`}>{period} days</button>)}
        </div></div>
        <TwitchBroadcasts key={stats.user.id || stats.user.login} videos={videos} days={days} />
      </>}
    </>}
  </section>;
}
